// Plain-language descriptions of model changes and migration operations.
import type { MigrationOperation } from '../ef/operations';
import type { JsonValue } from '../ef/values';
import { rootOfPath } from '../model/logical';
import type { ModelSpec, PropertySpec, RelationshipSpec } from '../model/schema';
import { changeEntity, type EntityFacets, type MatchHow, type ModelChange } from './diff';

/** How something changed between two models. */
export type ChangeState = 'added' | 'removed' | 'changed';

/** A property's type as shown in diagrams and change lists: `string(100)?`, `decimal(18,2)`, `int[]`. */
export function typeLabel(p: PropertySpec): string {
  let t = p.type;
  if (p.maxLength !== undefined) t += `(${p.maxLength})`;
  else if (p.precision !== undefined) t += p.scale !== undefined ? `(${p.precision},${p.scale})` : `(${p.precision})`;
  if (p.collection) t += '[]';
  return p.required ? t : `${t}?`;
}

export interface ChangeText {
  text: string;
  /** Secondary text: a type, a foreign key, how a rename was found. */
  detail?: string;
  state: ChangeState;
}

function show(v: unknown): string {
  if (v === null || v === undefined) return 'none';
  if (typeof v === 'string') return v;
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  return JSON.stringify(v);
}

export function howFound(how: MatchHow, confidence: number): string {
  if (how === 'declared') return 'declared rename';
  if (how === 'migration') return 'renamed by the migration';
  if (how === 'guess') return `guessed, ${Math.round(confidence * 100)}% sure`;
  return '';
}

const GENERATED: Record<string, string> = {
  none: 'none',
  onAdd: 'on insert',
  onUpdate: 'on update',
  onAddOrUpdate: 'on insert and update',
  onUpdateSometimes: 'on some updates',
};

const SHAPE = new Set(['type', 'required', 'maxLength', 'precision', 'scale', 'collection']);

/** What changed about a property: `string(100) → string(200)`, `value generation on insert → none`. */
export function propertyChangeNote(before: PropertySpec, after: PropertySpec, facets: string[]): string {
  const parts: string[] = [];
  if (facets.some((f) => SHAPE.has(f))) parts.push(`${typeLabel(before)} → ${typeLabel(after)}`);
  if (facets.includes('generated')) parts.push(`value generation ${GENERATED[before.generated ?? 'none']} → ${GENERATED[after.generated ?? 'none']}`);
  if (facets.includes('concurrencyToken')) parts.push(after.concurrencyToken ? 'now a concurrency token' : 'no longer a concurrency token');
  if (facets.includes('default')) parts.push(`default ${show(before.default)} → ${show(after.default)}`);
  if (facets.includes('comment')) parts.push('comment changed');
  if (facets.includes('physical')) parts.push('database details changed');
  return parts.join('; ');
}

function entityFacetText(facet: string, before: Partial<EntityFacets>, after: Partial<EntityFacets>): string {
  const b = before[facet as keyof EntityFacets];
  const a = after[facet as keyof EntityFacets];
  switch (facet) {
    case 'base':
      return a ? (b ? `base type ${show(b)} → ${show(a)}` : `now derives from ${show(a)}`) : `no longer derives from ${show(b)}`;
    case 'strategy':
      return `inheritance ${b ? String(b).toUpperCase() : 'none'} → ${a ? String(a).toUpperCase() : 'none'}`;
    case 'keyless':
      return a ? 'now keyless' : 'now has a key';
    case 'json':
      return a ? `stored as JSON in ${show(a)}` : 'no longer stored as JSON';
    case 'comment':
      return 'comment changed';
    case 'discriminator':
      return a === null || a === undefined ? `discriminator ${show(b)} removed` : b === null || b === undefined ? `discriminator ${show(a)}` : `discriminator ${show(b)} → ${show(a)}`;
    case 'discriminatorValue':
      return a === null || a === undefined ? 'no discriminator value' : b === null || b === undefined ? `discriminator value ${show(a)}` : `discriminator value ${show(b)} → ${show(a)}`;
    case 'table':
      return a === null || a === undefined ? `no longer mapped to table ${show(b)}` : b === null || b === undefined ? `now mapped to table ${show(a)}` : `table ${show(b)} → ${show(a)}`;
    case 'physical':
      return 'database details changed';
    default:
      return `${facet} ${show(b)} → ${show(a)}`;
  }
}

function relationshipFacetText(facet: string, b: RelationshipSpec, a: RelationshipSpec): string {
  switch (facet) {
    case 'cardinality':
      return `${b.cardinality} → ${a.cardinality}`;
    case 'required':
      return a.required ? 'now required' : 'now optional';
    case 'requiredDependent':
      return a.requiredDependent ? 'dependent now required' : 'dependent now optional';
    case 'onDelete':
      return `on delete ${b.onDelete} → ${a.onDelete}`;
    case 'dependentNavigation':
      return `navigation ${show(b.dependentNavigation)} → ${show(a.dependentNavigation)}`;
    case 'principalNavigation':
      return `inverse navigation ${show(b.principalNavigation)} → ${show(a.principalNavigation)}`;
    case 'principalKey':
      return `references ${show(b.principalKey?.join(', '))} → ${show(a.principalKey?.join(', '))}`;
    default:
      return 'database details changed';
  }
}

/** `path` relative to the card it is shown on: `Order.Shipping` under `Order` is `Shipping`. */
function inside(root: string, path: string, name?: string): string {
  const rel = path === root ? '' : path.startsWith(`${root}.`) ? path.slice(root.length + 1) : path;
  return [rel, name].filter(Boolean).join('.');
}

const list = (names: readonly string[]) => names.join(', ');

/** Describes a change as a line under its entity (`root`). */
export function describeChange(c: ModelChange, root: string): ChangeText {
  switch (c.kind) {
    case 'entity-added':
      return { text: 'New entity', state: 'added' };
    case 'entity-removed':
      return { text: 'Removed', state: 'removed' };
    case 'entity-renamed':
      return { text: `Renamed from ${c.from}`, detail: howFound(c.how, c.confidence), state: 'changed' };
    case 'entity-changed':
    case 'inheritance-changed': {
      const where = inside(root, c.entity);
      const text = c.facets.map((f) => entityFacetText(f, c.before, c.after)).join('; ');
      return { text: where ? `${where}: ${text}` : text.charAt(0).toUpperCase() + text.slice(1), state: 'changed' };
    }
    case 'property-added':
      return { text: `Added ${inside(root, c.entity, c.property)}`, detail: typeLabel(c.spec), state: 'added' };
    case 'property-removed':
      return { text: `Removed ${inside(root, c.entity, c.property)}`, detail: typeLabel(c.spec), state: 'removed' };
    case 'property-renamed':
      return { text: `Renamed ${inside(root, c.entity, c.from)} to ${c.to}`, detail: howFound(c.how, c.confidence), state: 'changed' };
    case 'property-changed':
      return { text: `${inside(root, c.entity, c.property)}: ${propertyChangeNote(c.before, c.after, c.facets)}`, state: 'changed' };
    case 'key-changed':
      return { text: `Primary key (${list(c.before)}) → (${list(c.after)})`, state: 'changed' };
    case 'key-added':
      return { text: `Added alternate key (${list(c.properties)})`, state: 'added' };
    case 'key-removed':
      return { text: `Removed alternate key (${list(c.properties)})`, state: 'removed' };
    case 'index-added':
      return { text: `Added ${c.spec.unique ? 'unique ' : ''}index on (${list(c.properties)})`, detail: c.spec.filter ? `where ${c.spec.filter}` : undefined, state: 'added' };
    case 'index-removed':
      return { text: `Removed ${c.spec.unique ? 'unique ' : ''}index on (${list(c.properties)})`, state: 'removed' };
    case 'index-changed':
      return { text: `Index on (${list(c.properties)}): ${c.facets.join(', ')} changed`, state: 'changed' };
    case 'check-added':
      return { text: `Added check ${c.name}`, detail: c.sql, state: 'added' };
    case 'check-removed':
      return { text: `Removed check ${c.name}`, detail: c.sql, state: 'removed' };
    case 'check-changed':
      return { text: `Check ${c.name} changed`, detail: `${c.before} → ${c.after}`, state: 'changed' };
    case 'seed-changed':
      return { text: `Seed data: ${c.before} → ${c.after} rows`, state: 'changed' };
    case 'owned-added':
      return { text: `Added owned ${inside(root, c.entity, c.navigation)}`, detail: c.type, state: 'added' };
    case 'owned-removed':
      return { text: `Removed owned ${inside(root, c.entity, c.navigation)}`, detail: c.type, state: 'removed' };
    case 'owned-changed':
      return { text: `Owned ${inside(root, c.entity, c.navigation)}: ${c.facets.join(', ')} changed`, state: 'changed' };
    case 'complex-added':
      return { text: `Added complex ${inside(root, c.entity, c.property)}`, detail: c.type, state: 'added' };
    case 'complex-removed':
      return { text: `Removed complex ${inside(root, c.entity, c.property)}`, detail: c.type, state: 'removed' };
    case 'complex-changed':
      return { text: `Complex ${inside(root, c.entity, c.property)}: ${c.facets.join(', ')} changed`, state: 'changed' };
    case 'relationship-added':
      return { text: `Added relationship to ${c.principal}`, detail: `foreign key ${list(c.foreignKey)}`, state: 'added' };
    case 'relationship-removed':
      return { text: `Removed relationship to ${c.principal}`, detail: `foreign key ${list(c.foreignKey)}`, state: 'removed' };
    case 'relationship-changed':
      return { text: `Relationship to ${c.principal}: ${c.facets.map((f) => relationshipFacetText(f, c.before, c.after)).join('; ')}`, state: 'changed' };
    case 'many-to-many-added':
      return { text: `Added many-to-many ${c.between[0]} ↔ ${c.between[1]}`, detail: `join ${c.join}`, state: 'added' };
    case 'many-to-many-removed':
      return { text: `Removed many-to-many ${c.between[0]} ↔ ${c.between[1]}`, detail: `join ${c.join}`, state: 'removed' };
    case 'sequence-added':
      return { text: `Added sequence ${c.name}`, state: 'added' };
    case 'sequence-removed':
      return { text: `Removed sequence ${c.name}`, state: 'removed' };
    case 'sequence-changed':
      return { text: `Sequence ${c.name}: ${c.facets.join(', ')} changed`, state: 'changed' };
  }
}

// ------------------------------------------------------------------ grouping

export interface ChangeGroup {
  /** Entity key (in the newer model, or the older one when removed); '' for model-level changes. */
  key: string;
  state: ChangeState;
  note?: string;
  items: ChangeText[];
}

const ORDER: Record<ChangeState, number> = { added: 0, changed: 1, removed: 2 };

/** Groups a step's changes by the entity card they belong to. */
export function groupChanges(changes: ModelChange[], after: ModelSpec, before: ModelSpec): ChangeGroup[] {
  const groups = new Map<string, ChangeGroup>();
  const group = (key: string, state: ChangeState) => {
    let g = groups.get(key);
    if (!g) {
      g = { key, state, items: [] };
      groups.set(key, g);
    } else if (state !== 'changed') {
      g.state = state;
    }
    return g;
  };
  const rootOf = (path: string) => (after.entities[rootOfPath(after, path).root] ? rootOfPath(after, path).root : rootOfPath(before, path).root);
  for (const c of changes) {
    if (c.kind === 'entity-added' || c.kind === 'entity-removed') {
      group(c.entity, c.kind === 'entity-added' ? 'added' : 'removed');
      continue;
    }
    if (c.kind === 'many-to-many-added' || c.kind === 'many-to-many-removed') {
      group(c.join, c.kind === 'many-to-many-added' ? 'added' : 'removed').items.push(describeChange(c, c.join));
      continue;
    }
    if (c.kind === 'entity-renamed') {
      const g = group(c.to, 'changed');
      g.note = `was ${c.from}`;
      g.items.unshift(describeChange(c, c.to));
      continue;
    }
    const path = changeEntity(c);
    const key = path === null ? '' : rootOf(path);
    group(key, 'changed').items.push(describeChange(c, key));
  }
  return [...groups.values()].sort((a, b) => (a.key === '' ? 1 : b.key === '' ? -1 : ORDER[a.state] - ORDER[b.state] || a.key.localeCompare(b.key)));
}

// ------------------------------------------------------------------ database operations

export interface OperationText {
  text: string;
  detail?: string;
  /** Why this operation deserves attention (custom SQL, hand-written conditions). */
  flag?: string;
}

function arg(v: JsonValue | undefined): string {
  if (v === undefined || v === null) return '';
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) return v.map(arg).join(', ');
  if (typeof v === 'object') return 'expr' in v ? String(v['expr']) : JSON.stringify(v);
  return String(v);
}

function table(name: JsonValue | undefined, schema: JsonValue | undefined): string {
  return schema ? `${arg(schema)}.${arg(name)}` : arg(name);
}

function describeOp(op: MigrationOperation): OperationText {
  const a = op.args;
  const t = table(a['table'], a['schema']);
  const columns = arg(a['columns'] ?? a['column']);
  switch (op.kind) {
    case 'CreateTable':
      return { text: `Create table ${table(a['name'], a['schema'])}`, detail: `${op.columns?.length ?? 0} columns` };
    case 'DropTable':
      return { text: `Drop table ${table(a['name'], a['schema'])}`, flag: 'Deletes the table and its rows' };
    case 'RenameTable':
      return { text: `Rename table ${table(a['name'], a['schema'])} to ${arg(a['newName'])}` };
    case 'AddColumn':
      return { text: `Add column ${arg(a['name'])} to ${t}`, detail: op.clrType ?? undefined };
    case 'DropColumn':
      return { text: `Drop column ${arg(a['name'])} from ${t}`, flag: 'Deletes the data in this column' };
    case 'AlterColumn':
      return { text: `Alter column ${arg(a['name'])} in ${t}`, detail: op.clrType ?? undefined };
    case 'RenameColumn':
      return { text: `Rename column ${arg(a['name'])} to ${arg(a['newName'])} in ${t}` };
    case 'CreateIndex':
      return { text: `Create ${a['unique'] === true ? 'unique ' : ''}index ${arg(a['name'])} on ${t}`, detail: columns || undefined };
    case 'DropIndex':
      return { text: `Drop index ${arg(a['name'])}${t ? ` on ${t}` : ''}` };
    case 'RenameIndex':
      return { text: `Rename index ${arg(a['name'])} to ${arg(a['newName'])}` };
    case 'AddForeignKey':
      return { text: `Add foreign key ${arg(a['name'])} on ${t}`, detail: `references ${table(a['principalTable'], a['principalSchema'])}` };
    case 'DropForeignKey':
      return { text: `Drop foreign key ${arg(a['name'])} on ${t}` };
    case 'AddPrimaryKey':
      return { text: `Add primary key ${arg(a['name'])} on ${t}`, detail: columns || undefined };
    case 'DropPrimaryKey':
      return { text: `Drop primary key ${arg(a['name'])} on ${t}` };
    case 'AddUniqueConstraint':
      return { text: `Add unique constraint ${arg(a['name'])} on ${t}`, detail: columns || undefined };
    case 'DropUniqueConstraint':
      return { text: `Drop unique constraint ${arg(a['name'])} on ${t}` };
    case 'AddCheckConstraint':
      return { text: `Add check ${arg(a['name'])} on ${t}`, detail: arg(a['sql']) };
    case 'DropCheckConstraint':
      return { text: `Drop check ${arg(a['name'])} on ${t}` };
    case 'InsertData':
      return { text: `Insert data into ${t}` };
    case 'UpdateData':
      return { text: `Update data in ${t}` };
    case 'DeleteData':
      return { text: `Delete data from ${t}` };
    case 'Sql': {
      const sql = arg(a['sql']).replace(/\s+/g, ' ').trim();
      return { text: 'Custom SQL', detail: sql.length > 140 ? `${sql.slice(0, 140)}…` : sql, flag: 'Custom SQL is not part of the model' };
    }
    case 'EnsureSchema':
      return { text: `Ensure schema ${arg(a['name'])}` };
    case 'DropSchema':
      return { text: `Drop schema ${arg(a['name'])}` };
    case 'CreateSequence':
      return { text: `Create sequence ${table(a['name'], a['schema'])}`, detail: op.clrType ?? undefined };
    case 'DropSequence':
      return { text: `Drop sequence ${table(a['name'], a['schema'])}` };
    case 'AlterSequence':
    case 'RestartSequence':
    case 'RenameSequence':
      return { text: `${op.kind.replace('Sequence', '')} sequence ${table(a['name'], a['schema'])}` };
    case 'AlterDatabase':
      return { text: 'Change database settings' };
    case 'AlterTable':
      return { text: `Alter table ${table(a['name'], a['schema'])}` };
    default:
      return { text: `${op.kind} ${arg(a['name'] ?? a['table'])}`.trim() };
  }
}

export function describeOperation(op: MigrationOperation): OperationText {
  const d = describeOp(op);
  if (op.conditional) return { ...d, flag: d.flag ?? 'Runs only under a hand-written condition' };
  return d;
}
